import { LoadingButton } from "@mui/lab";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { CheckoutState, SubscriptionContext } from "contexts/Subscription";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddOnItemName } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { getParams, getSource } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import styles from "./styles";

const PACKS: { name: AddOnItemName; badge?: string; popular?: boolean }[] = [
  { name: AddOnItemName.ai_credits_300 },
  { name: AddOnItemName.ai_credits_750, badge: "MOST POPULAR", popular: true },
  { name: AddOnItemName.ai_credits_2000, badge: "BEST VALUE · SAVE 40%" },
];

const CreditPacks = () => {
  const source = getSource();
  const navigate = useNavigate();
  const { addOns, setOpenCheckoutDialog, upgradeDialogSource } =
    useContext(SubscriptionContext);
  const { checkoutLoading } = useSubscription();
  const [selected, setSelected] = useState<AddOnItemName>(
    AddOnItemName.ai_credits_750,
  );

  if (!addOns?.[AddOnItemName.ai_credits_750]) {
    return <></>;
  }

  const selectedDetails = addOns?.[selected];

  const handleCheckout = async () => {
    if (!addOns) return;
    const state: CheckoutState = {
      plan: selected,
      source,
      pageSource: window.location.pathname,
      coupon: getParams("coupon"),
    };
    if (upgradeDialogSource) {
      setOpenCheckoutDialog(state);
    } else {
      navigate(`/${routes.subscriptions}`, { state });
    }
  };

  return (
    <>
      <Stack spacing={1.25} sx={styles.options}>
        {PACKS.map(({ name, badge, popular }) => {
          const details = addOns?.[name];
          if (!details?.credits) return null;
          const perCredit = details.price / details.credits;
          const isSelected = selected === name;
          return (
            <Stack
              key={name}
              direction="row"
              alignItems="center"
              spacing={1.5}
              onClick={() => setSelected(name)}
              sx={styles.option(isSelected)}
            >
              <Box sx={styles.radio(isSelected)}>
                {isSelected && <Box sx={styles.radioDot} />}
              </Box>

              <Box sx={styles.optionMain}>
                <Typography sx={styles.optionCredits}>
                  {details.credits.toLocaleString()}{" "}
                  <Box component="span" sx={styles.optionCreditsUnit}>
                    credits
                  </Box>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ${perCredit.toFixed(3)} / credit
                </Typography>
              </Box>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                sx={styles.optionRight}
              >
                {badge && (
                  <Chip
                    label={badge}
                    size="small"
                    sx={styles.badge(!!popular)}
                  />
                )}
                <Typography sx={styles.price}>
                  ${details.price.toFixed(0)}
                </Typography>
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      <LoadingButton
        fullWidth
        onClick={handleCheckout}
        disableElevation
        variant="contained"
        loading={checkoutLoading === selected}
        sx={styles.button}
      >
        Buy {selectedDetails?.credits?.toLocaleString()} credits · $
        {selectedDetails?.price?.toFixed(0)}
      </LoadingButton>
      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        sx={styles.disclaimer}
      >
        One-time purchase · credits never expire.{" "}
        <strong>Volume discounts</strong> on larger packs.
      </Typography>
    </>
  );
};

export default CreditPacks;
